import React, { useRef } from "react";
import { Box, Flex, Text, Loader } from "@mantine/core";
import {
  IconChevronLeft,
  IconChevronRight,
  IconDownload,
  IconPlus,
  IconSettings,
  IconEdit,
  IconTrash,
  IconX,
  IconFileText,
  IconShieldCheck,
} from "@tabler/icons-react";
import useSharedStyles from "../sharedStyles";
import useDetailStyles from "../detailStyles";
import StatusPill from "../components/StatusPill";
import PillToggle from "../components/PillToggle";
import PurpleSwitch from "../components/PurpleSwitch";
import { REPORT_SOURCE, REPORT_STATUS } from "../theme";
import { isImageSignature } from "../../../../components/QuarterlyReportDrawer/pdfHelpers";
import RangePickerField from "../../../../components/QuarterlyReportDrawer/components/RangePickerField";
import DropdownSelect from "../../../../components/QuarterlyReportDrawer/components/DropdownSelect";
import { formatRange } from "../utils";

const STATUS_OPTIONS = [
  { value: "achieved", label: "Achieved" },
  { value: "on-track", label: "On track" },
  { value: "limited", label: "Limited or no progress" },
];
// Only offered when the plan never classified the row. Where the plan does say,
// the risk type is shown as read-only plan data — essentialSupportType is a
// fixed three-option Select in the ISP editor, so it is not a judgement to be
// re-made here. "Routine Support" is absent on purpose: a routine support is
// not a risk row at all, so choosing it here could not move the row.
const RISK_TYPE_OPTIONS = [
  { value: "identified", label: "Identified Risk" },
  { value: "potential", label: "Potential Risk" },
];
const RISK_TYPE_LABELS = {
  identified: "Identified Risk",
  potential: "Potential Risk",
};
const YN_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

const rowAnchorId = (section, ref) => `qpcr-row-${section}-${ref}`;

const formatDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? String(v)
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

// A status the engine could not evidence is left blank rather than guessed —
// say so where the pill would otherwise read as an answered field.
const RequiresSelection = ({ classes, manual }) =>
  manual ? null : (
    <Text className={classes.outcomeMeta} mt={6}>
      Requires QDDP selection
    </Text>
  );

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp"];
const isImageFile = (name = "") => IMAGE_EXTENSIONS.includes(name.split(".").pop()?.toLowerCase());

// Reference material attached to a manual report: files pulled out of the
// provider's own EHR so the reviewer can cite them while typing the sections.
// Explicitly NOT an AI input — the banner says so, because that is the whole
// distinction between this document type and a Tara Ai draft.
const DEFAULT_EVIDENCE_TYPES = ["pdf", "doc", "docx", "xls", "xlsx", "jpg", "jpeg", "png", "heic", "heif"];

const EvidenceCard = ({ files, locked, busy, onUpload, onRemove, classes, cx, evidenceTypes }) => {
  const inputRef = useRef(null);
  const allowed = evidenceTypes?.length ? evidenceTypes : DEFAULT_EVIDENCE_TYPES;
  const accept = allowed.map((t) => `.${t}`).join(",");
  return (
    <>
      <Flex className={classes.infoBanner}>
        <IconShieldCheck size={16} stroke={2} style={{ flexShrink: 0, marginTop: 1 }} />
        <span>
          Upload images and documents from the provider&rsquo;s EHR to reference while you fill in the
          review. These files are <strong>not read by AI</strong> and are stored with the report.
        </span>
      </Flex>
      <Box className={classes.eviStrip}>
        {(files || []).map((f) => (
          <Box key={f.id} className={classes.eviItem}>
            {!locked && (
              <button
                className={classes.eviRemove}
                onClick={() => onRemove(f.id)}
                aria-label={`Remove ${f.name}`}
              >
                <IconX size={12} />
              </button>
            )}
            <a href={f.fileUrl} target="_blank" rel="noreferrer">
              <Box className={cx(classes.eviThumb, !isImageFile(f.name) && classes.eviThumbDoc)}>
                {isImageFile(f.name) ? (
                  <img src={f.fileUrl} alt={f.name} className={classes.eviThumbImg} />
                ) : (
                  <IconFileText size={24} stroke={1.7} />
                )}
              </Box>
            </a>
            <Text className={classes.eviCap} title={f.name}>
              {f.name}
            </Text>
          </Box>
        ))}
        {!locked && (
          <>
            <button
              className={classes.eviAdd}
              onClick={() => inputRef.current?.click()}
              aria-label="Add evidence"
            >
              {busy ? <Loader size={16} color="violet" /> : <IconPlus size={20} stroke={2} />}
            </button>
            <input
              ref={inputRef}
              type="file"
              hidden
              accept={accept}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) onUpload(file);
              }}
            />
          </>
        )}
        {locked && !files?.length && (
          <Text size="xs" color="dimmed">
            No evidence was attached to this report.
          </Text>
        )}
      </Box>
    </>
  );
};

// A removable section row's header: an editable title on a manual report,
// read-only plan text on a drafted one.
const RowHeading = ({ value, placeholder, onChange, locked, manual, onRemove, canRemove, classes, children }) =>
  manual ? (
    <Flex gap={8} align="flex-start">
      <input
        className={cxTitle(classes)}
        placeholder={placeholder}
        value={value || ""}
        disabled={locked}
        onChange={(e) => onChange(e.target.value)}
      />
      {!locked && canRemove && (
        <button className={classes.rowRemoveBtn} onClick={onRemove} aria-label="Remove row">
          <IconTrash size={15} />
        </button>
      )}
    </Flex>
  ) : (
    children
  );

const cxTitle = (classes) => `${classes.inp} ${classes.inpTitle}`;

// The prototype's "Plan change needed" is a real on/off switch (.inline-toggle
// + .tgl), not a Yes/No segmented pill — PillToggle is for choosing between
// named options, this is a single boolean. Reuses PurpleSwitch, the same
// switch component the Settings page's "Tara Ai Drafting" panel already uses
// for its on/off rows — its checked color is forced to QR.accent (amber),
// matching the mockup's .tgl.on exactly, despite the component's name.
const PlanChangeToggle = ({ value, onChange, locked, classes }) => (
  <Flex className={classes.inlineToggle}>
    <PurpleSwitch checked={!!value} disabled={locked} onChange={(e) => onChange(e.currentTarget.checked)} />
    Plan change needed
  </Flex>
);

// Lists every flag the engine raised, each linking to the field it concerns.
const FlagBanner = ({ flags, classes }) => {
  if (!flags?.length) return null;
  const jumpTo = (flag) => {
    const el = document.getElementById(rowAnchorId(flag.section, flag.ref));
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  return (
    <Box className={classes.flagBanner}>
      <Text className={classes.flagBannerTitle}>
        {flags.length} selection{flags.length === 1 ? "" : "s"} need your review
      </Text>
      <Text className={classes.flagBannerLead}>
        These could not be evidenced from the documentation, so they were left for you to decide.
        You can confirm them when you sign.
      </Text>
      {flags.map((f, i) => (
        <Box
          key={`${f.code}-${f.ref}-${i}`}
          className={classes.flagItem}
          role="button"
          tabIndex={0}
          onClick={() => jumpTo(f)}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && jumpTo(f)}
        >
          <Text className={classes.flagItemSection}>Section {f.section}</Text>
          <Text className={classes.flagItemDetail}>{f.detail}</Text>
        </Box>
      ))}
    </Box>
  );
};

const OutcomeBlock = ({ outcome, onChange, locked, manual, onRemove, canRemove }) => {
  const { classes } = useDetailStyles();
  return (
    <Box className={classes.outcome} id={rowAnchorId("A", outcome.id)}>
      <RowHeading
        manual={manual}
        locked={locked}
        canRemove={canRemove}
        onRemove={onRemove}
        classes={classes}
        value={outcome.title}
        placeholder="Outcome — e.g. John will prepare a simple meal independently twice a week"
        onChange={(v) => onChange({ title: v })}
      >
        <Flex className={classes.outcomeTop}>
          <Box>
            <Text className={classes.outcomeTitle}>{outcome.title}</Text>
            <Text className={classes.outcomeMeta}>{outcome.meta}</Text>
          </Box>
          <span className={classes.provTag}>Drafted from {outcome.noteCount} notes</span>
        </Flex>
      </RowHeading>

      <Box>
        <Text className={classes.fldLabel} mb={6}>
          Status of outcome
        </Text>
        <PillToggle
          options={STATUS_OPTIONS}
          value={outcome.status}
          onChange={(v) => onChange({ status: v })}
          disabled={locked}
        />
        {outcome.status == null && <RequiresSelection classes={classes} manual={manual} />}
      </Box>

      <Box>
        <Text className={classes.fldLabel} mb={6}>
          Status description
        </Text>
        <textarea
          className={classes.ta}
          placeholder="Describe progress this period, citing dates from your evidence."
          value={outcome.description}
          disabled={locked}
          onChange={(e) => onChange({ description: e.target.value })}
        />
      </Box>

      <PlanChangeToggle
        value={outcome.planChangeNeeded}
        onChange={(v) => onChange({ planChangeNeeded: v })}
        locked={locked}
        classes={classes}
      />

      {outcome.planChangeNeeded === true && (
        <Box>
          <Text className={classes.fldLabel} mb={6}>
            If yes, describe
          </Text>
          <textarea
            className={classes.ta}
            placeholder="Describe the plan change needed."
            value={outcome.planChangeDescription}
            disabled={locked}
            onChange={(e) => onChange({ planChangeDescription: e.target.value })}
          />
        </Box>
      )}
    </Box>
  );
};

const RiskBlock = ({ risk, onChange, locked, manual, onRemove, canRemove }) => {
  const { classes } = useDetailStyles();
  const period = risk.effectivePeriods;
  return (
    <Box className={classes.outcome} id={rowAnchorId("B", risk.id)}>
      <RowHeading
        manual={manual}
        locked={locked}
        canRemove={canRemove}
        onRemove={onRemove}
        classes={classes}
        value={risk.title}
        placeholder="Risk and how it is supported"
        onChange={(v) => onChange({ title: v })}
      >
        <Flex className={classes.outcomeTop}>
          <Box>
            {/* The heading is the support statement — what the risk actually is
                and how to support it — not the category it falls under. */}
            <Text className={classes.outcomeTitle}>{risk.title}</Text>
            <Text className={classes.outcomeMeta}>
              {[risk.meta, risk.howOften && `Frequency: ${risk.howOften}`].filter(Boolean).join(" · ")}
            </Text>
          </Box>
          <span className={classes.provTag}>Drafted from {risk.noteCount} notes</span>
        </Flex>
      </RowHeading>

      <Box className={classes.formGrid}>
        <Box>
          <Text className={classes.fldLabel} mb={6}>
            Risk type
          </Text>
          {risk.riskType && !manual ? (
            <Text className={classes.metaValue}>{RISK_TYPE_LABELS[risk.riskType]}</Text>
          ) : (
            <>
              <PillToggle
                options={RISK_TYPE_OPTIONS}
                value={risk.riskType}
                onChange={(v) => onChange({ riskType: v })}
                disabled={locked}
              />
              {risk.riskType == null && <RequiresSelection classes={classes} manual={manual} />}
            </>
          )}
        </Box>
        <Box>
          <Text className={classes.fldLabel} mb={6}>
            Status
          </Text>
          <PillToggle
            options={STATUS_OPTIONS}
            value={risk.status}
            onChange={(v) => onChange({ status: v })}
            disabled={locked}
          />
          {risk.status == null && <RequiresSelection classes={classes} manual={manual} />}
        </Box>
      </Box>

      {!manual && (
        <Box className={classes.formGrid}>
          <Box>
            <Text className={classes.metaLabel}>Start date</Text>
            <Text className={classes.metaValue}>{formatDate(period?.start)}</Text>
          </Box>
          <Box>
            <Text className={classes.metaLabel}>End date</Text>
            <Text className={classes.metaValue}>{formatDate(period?.end)}</Text>
          </Box>
        </Box>
      )}

      <Box>
        <Text className={classes.fldLabel} mb={6}>
          Status description
        </Text>
        <textarea
          className={classes.ta}
          placeholder="Describe the risk and how it was supported this period."
          value={risk.description}
          disabled={locked}
          onChange={(e) => onChange({ description: e.target.value })}
        />
      </Box>

      <PlanChangeToggle
        value={risk.planChangeNeeded}
        onChange={(v) => onChange({ planChangeNeeded: v })}
        locked={locked}
        classes={classes}
      />

      {risk.planChangeNeeded === true && (
        <Box>
          <Text className={classes.fldLabel} mb={6}>
            If yes, describe
          </Text>
          <textarea
            className={classes.ta}
            placeholder="Describe the plan change needed."
            value={risk.planChangeDescription}
            disabled={locked}
            onChange={(e) => onChange({ planChangeDescription: e.target.value })}
          />
        </Box>
      )}
    </Box>
  );
};

const ReviewDetailView = ({
  batch,
  index,
  draft,
  locked,
  saving,
  onChangeDraft,
  onPrev,
  onNext,
  onBack,
  onSaveNext,
  onOpenPdfPreview,
  onOpenSign,
  onGenerate,
  onOpenSettings,
  onSaveDraft,
  onUploadEvidence,
  onRemoveEvidence,
  evidenceTypes,
  providerOptions,
}) => {
  const { classes: shared } = useSharedStyles();
  const { classes, cx } = useDetailStyles();
  const isLast = index === batch.length - 1;
  // Manual reports are hand-entered for a provider documenting in its own EHR:
  // the fields an AI draft fills from the ISP and notes are typed in here, and
  // rows can be added or removed because no plan defines them.
  const isManual = draft.source === "manual";

  const patchOutcome = (idx, patch) => {
    if (locked) return;
    onChangeDraft((d) => ({
      ...d,
      outcomesA: d.outcomesA.map((o, i) => (i === idx ? { ...o, ...patch } : o)),
    }));
  };

  // Reports drafted before Section B became plural carry a single `supportB`.
  const risksB = draft.risksB?.length ? draft.risksB : draft.supportB ? [draft.supportB] : [];

  const patchRisk = (idx, patch) => {
    if (locked) return;
    onChangeDraft((d) => {
      const rows = d.risksB?.length ? d.risksB : d.supportB ? [d.supportB] : [];
      const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
      return { ...d, risksB: next, supportB: next[0] || null };
    });
  };

  const checklistIds = new Set((draft.checklistB || []).map((s) => s.id));
  const unmatchedRoutine = (draft.routineSupportsB || []).filter(
    (r) => r.key == null || !checklistIds.has(r.key)
  );

  const patchChecklist = (id, patch) => {
    if (locked) return;
    onChangeDraft((d) => ({
      ...d,
      checklistB: d.checklistB.map((s) => (s.id === id ? { ...s, ...patch } : s)),
    }));
  };

  const patchQuestion = (id, patch) => {
    if (locked) return;
    onChangeDraft((d) => ({
      ...d,
      questionsC: d.questionsC.map((q) => (q.id === id ? { ...q, ...patch } : q)),
    }));
  };

  const patchHeader = (patch) => {
    if (locked) return;
    onChangeDraft((d) => ({ ...d, ...patch }));
  };

  const addOutcome = () => {
    if (locked) return;
    onChangeDraft((d) => ({
      ...d,
      outcomesA: [
        ...(d.outcomesA || []),
        {
          id: "outcome-" + Date.now(),
          title: "",
          meta: "",
          noteCount: 0,
          status: null,
          description: "",
          planChangeNeeded: null,
          planChangeDescription: "",
        },
      ],
    }));
  };

  const removeOutcome = (idx) => {
    if (locked) return;
    onChangeDraft((d) => ({ ...d, outcomesA: (d.outcomesA || []).filter((_, i) => i !== idx) }));
  };

  const addRisk = () => {
    if (locked) return;
    onChangeDraft((d) => {
      const rows = d.risksB?.length ? d.risksB : d.supportB ? [d.supportB] : [];
      const next = [
        ...rows,
        {
          id: "support-" + Date.now(),
          title: "",
          meta: "",
          noteCount: 0,
          riskType: null,
          status: null,
          description: "",
          planChangeNeeded: null,
          planChangeDescription: "",
        },
      ];
      return { ...d, risksB: next, supportB: next[0] || null };
    });
  };

  const removeRisk = (idx) => {
    if (locked) return;
    onChangeDraft((d) => {
      const rows = d.risksB?.length ? d.risksB : d.supportB ? [d.supportB] : [];
      const next = rows.filter((_, i) => i !== idx);
      return { ...d, risksB: next, supportB: next[0] || null };
    });
  };

  return (
    <Box className={shared.content}>
      <Flex className={shared.crumbBar}>
        <button className={shared.backBtn} onClick={onBack}>
          <IconChevronLeft size={16} /> Back
        </button>
      </Flex>

      {/* A manual report is a single hand-entered document, not one row of a
          batch — the "Generate Quarterly" bar and the batch pager both only
          mean something for the AI flow, so neither shows here (matches the
          mockup: crumb-bar goes straight into the report itself). */}
      {!isManual && (
        <>
          <Flex className={shared.toolbarCard}>
            <button className={shared.btnAdd} onClick={onGenerate}>
              <Flex align="center" gap={8}>
                <IconPlus size={16} />
                Generate Quarterly
              </Flex>
            </button>
            <button className={shared.iconBtnSquare} onClick={onOpenSettings} aria-label="Report settings">
              <IconSettings size={18} />
            </button>
          </Flex>

          <Flex className={classes.pagerBar}>
            <Flex className={classes.pager}>
              <button className={classes.pagerArrow} disabled={index === 0} onClick={onPrev}>
                <IconChevronLeft size={15} />
              </button>
              <Box className={classes.pagerMid}>
                <Box className={classes.pagerCount}>
                  {index + 1} of {batch.length}
                </Box>
                <Flex className={classes.pagerDots}>
                  {batch.map((_, i) => (
                    <span
                      key={i}
                      className={cx(
                        classes.pagerDot,
                        i < index && classes.pagerDotDone,
                        i === index && classes.pagerDotOn
                      )}
                    />
                  ))}
                </Flex>
              </Box>
              <button className={classes.pagerArrow} disabled={isLast} onClick={onNext}>
                <IconChevronRight size={15} />
              </button>
            </Flex>
          </Flex>
        </>
      )}

      <Box className={classes.reportWrap}>
      <Box className={classes.cardSegment}>
        <Flex className={classes.reportToolbar}>
          <Flex className={classes.toolbarTitle}>
            {isManual ? "Manual Quarterly Review" : "Quarterly Review"}
            {isManual && <StatusPill config={{ ...REPORT_SOURCE.manual, label: "Manual · No AI" }} />}
            {!isManual && <StatusPill config={REPORT_SOURCE[draft.source] || REPORT_SOURCE.ai} />}
            {/* Same status pill the Reports list shows for this report
                ("Awaiting Review" for a draft, "Final" once signed) — kept
                consistent between the list and this page. Only once the
                report actually exists (draft.id set, i.e. saved at least
                once) — a blank, not-yet-created manual report has no status
                to report yet. */}
            {draft.id && <StatusPill config={REPORT_STATUS[draft.status]} />}
          </Flex>
          <Flex className={classes.toolbarActions}>
            <button className={classes.previewBtn} onClick={onOpenPdfPreview}>
              <IconDownload size={15} stroke={2} /> Preview & Download
            </button>
            {/* A manual report is a single document, not a batch to page
                through, so it only ever gets one save action — no
                Save & Next/Finish pager button. */}
            {isManual ? (
              !locked && (
                <button className={shared.btnAdd} disabled={saving} onClick={onSaveDraft}>
                  <Flex className={classes.saveBtnInner}>
                    {saving && <Loader size={13} color="currentColor" />}
                    {saving ? "Saving…" : "Save"}
                  </Flex>
                </button>
              )
            ) : (
            <button className={shared.btnAdd} disabled={saving} onClick={() => onSaveNext(isLast)}>
              <Flex className={classes.saveBtnInner}>
                {saving && <Loader size={13} color="currentColor" />}
                {saving
                  ? "Saving…"
                  : locked
                  ? isLast
                    ? "Finish"
                    : "Next"
                  : isLast
                  ? "Save & Finish"
                  : "Save & Next"}
              </Flex>
            </button>
            )}
          </Flex>
        </Flex>
        {locked && (
          <Flex className={classes.lockedBanner}>
            This report is finalized and locked for edits. Delete and regenerate to make changes.
          </Flex>
        )}
      </Box>

      <Box className={cx(classes.cardSegment, classes.cardSegmentNoTop)}>
        <Box className={cx(classes.cardBody, classes.cardBodyInWrap)}>
          {/* Manual reports use the prototype's "Report details" block: a 2-column
              .form-grid reading Individual → Facility across the first row, then
              Provider → Review period across the second. An AI-drafted report has
              no facility field and stays on the 3-column read-only .meta-grid. */}
          {isManual ? (
            <>
              <Text className={cx(classes.fldLabel, classes.reportDetailsLabel)}>Report details</Text>
              <Box className={classes.formGrid}>
                <Box>
                  <Text className={classes.metaLabel}>Individual name</Text>
                  <input
                    className={classes.inp}
                    placeholder="Type the individual's name"
                    value={draft.clientName || ""}
                    disabled={locked}
                    onChange={(e) => patchHeader({ clientName: e.target.value })}
                  />
                </Box>
                <Box>
                  <Text className={classes.metaLabel}>Facility / home (optional)</Text>
                  <input
                    className={classes.inp}
                    placeholder="Optional"
                    value={draft.facilityName || ""}
                    disabled={locked}
                    onChange={(e) => patchHeader({ facilityName: e.target.value })}
                  />
                </Box>
                <Box>
                  <Text className={classes.metaLabel}>Provider</Text>
                  {locked ? (
                    <Text className={classes.metaValue}>{draft.providerName}</Text>
                  ) : (
                    <DropdownSelect
                      value={draft.providerId}
                      options={providerOptions}
                      getMeta={(o) => o.ehr}
                      placeholder="Select a provider"
                      onSelect={(opt) => patchHeader({ providerId: opt.value, providerName: opt.label })}
                    />
                  )}
                </Box>
                <Box>
                  <Text className={classes.metaLabel}>Review period</Text>
                  {locked ? (
                    <Text className={classes.metaValue}>{draft.periodLabel}</Text>
                  ) : (
                    <RangePickerField
                      value={{ from: draft.periodStart, to: draft.periodEnd }}
                      onChange={(period) =>
                        patchHeader({
                          periodStart: period.from,
                          periodEnd: period.to,
                          periodLabel: formatRange(period.from, period.to),
                        })
                      }
                    />
                  )}
                </Box>
              </Box>
            </>
          ) : (
            <Box className={classes.metaGrid}>
              <Box>
                <Text className={classes.metaLabel}>Individual</Text>
                <Text className={classes.metaValue}>{draft.clientName}</Text>
              </Box>
              <Box>
                <Text className={classes.metaLabel}>Provider / Service</Text>
                <Text className={classes.metaValue}>{draft.providerName}</Text>
              </Box>
              <Box>
                <Text className={classes.metaLabel}>Review Period</Text>
                <Text className={classes.metaValue}>{draft.periodLabel}</Text>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {isManual && (
        <Box className={classes.cardSegment}>
          <Flex className={cx(classes.cardHead, classes.cardHeadInWrap)}>
            <Text className={classes.cardHeadTitle}>Evidence</Text>
            <Text className={classes.cardHeadHint}>Reference only</Text>
          </Flex>
          <Box className={cx(classes.cardBody, classes.cardBodyInWrap)}>
            <EvidenceCard
              files={draft.evidenceFiles}
              locked={locked}
              busy={saving}
              onUpload={onUploadEvidence}
              onRemove={onRemoveEvidence}
              classes={classes}
              cx={cx}
              evidenceTypes={evidenceTypes}
            />
          </Box>
        </Box>
      )}

      <FlagBanner flags={draft.flags} classes={classes} />

      <Box className={classes.cardSegment}>
        <Flex className={cx(classes.cardHead, classes.cardHeadInWrap)}>
          <Text className={classes.cardHeadTitle}>Section A · Desired Outcomes</Text>
          <Text className={classes.cardHeadHint}>
            {isManual ? "Enter each ISP outcome" : "Status of each ISP outcome for the period"}
          </Text>
        </Flex>
        <Box className={cx(classes.cardBody, classes.cardBodyInWrap)}>
          {draft.outcomesA.map((o, i) => (
            <OutcomeBlock
              key={o.id}
              outcome={o}
              onChange={(patch) => patchOutcome(i, patch)}
              locked={locked}
              manual={isManual}
              onRemove={() => removeOutcome(i)}
              canRemove={draft.outcomesA.length > 1}
            />
          ))}
          {isManual && !locked && (
            <button className={classes.addBtn} onClick={addOutcome}>
              <IconPlus size={15} stroke={2.4} />
              Add outcome
            </button>
          )}
        </Box>
      </Box>

      <Box className={classes.cardSegment}>
        <Flex className={cx(classes.cardHead, classes.cardHeadInWrap)}>
          <Text className={classes.cardHeadTitle}>Section B · Essential Supports</Text>
          <Text className={classes.cardHeadHint}>
            {isManual
              ? "Enter risks and check the routine supports provided"
              : "Risks and routine supports for the period"}
          </Text>
        </Flex>
        <Box className={cx(classes.cardBody, classes.cardBodyInWrap)}>
          {/* One row per plan essential support classified as a risk. Routine
              Support rows are not risks and render in the checklist below. */}
          {risksB.map((r, i) => (
            <RiskBlock
              key={r.id}
              risk={r}
              onChange={(patch) => patchRisk(i, patch)}
              locked={locked}
              manual={isManual}
              onRemove={() => removeRisk(i)}
              canRemove={risksB.length > 1}
            />
          ))}
          {isManual && !locked && (
            <button className={classes.addBtn} onClick={addRisk}>
              <IconPlus size={15} stroke={2.4} />
              Add risk
            </button>
          )}

          <Box>
            <Text className={classes.fldLabel} mt={4} mb={10}>
              Routine supports documented this period
            </Text>
            <Box className={classes.supportGrid}>
              {draft.checklistB.map((s) => (
                <Box key={s.id} className={cx(classes.supportItem, s.checked && classes.supportItemChecked)}>
                  <Flex
                    className={classes.supportRow}
                    onClick={() => !locked && patchChecklist(s.id, { checked: !s.checked })}
                  >
                    <Box className={cx(classes.cbx, s.checked && classes.cbxOn)} />
                    {s.label}
                  </Flex>
                  {s.checked && (
                    <textarea
                      className={classes.supportInputTa}
                      placeholder="Describe how this support was provided this period..."
                      value={s.note}
                      disabled={locked}
                      onChange={(e) => patchChecklist(s.id, { note: e.target.value })}
                    />
                  )}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Plan rows classified "Routine Support" whose free-text wording
              matched no checklist key. Listed rather than dropped, so the plan
              row stays visible to the QDDP. */}
          {unmatchedRoutine.length > 0 && (
            <Box mt={16}>
              <Text className={classes.fldLabel} mb={10}>
                Routine supports on the plan without a matching checklist entry
              </Text>
              {unmatchedRoutine.map((r) => (
                <Box key={r.id} className={classes.outcome} id={rowAnchorId("B", r.id)}>
                  <Text className={classes.outcomeTitle}>{r.label}</Text>
                  <Text className={classes.outcomeMeta}>
                    {[r.howOften && `Frequency: ${r.howOften}`, r.checked ? "Documented this period" : "Not documented this period"]
                      .filter(Boolean)
                      .join(" · ")}
                  </Text>
                  {r.note && <Text className={classes.metaValue}>{r.note}</Text>}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      <Box className={classes.cardSegment}>
        <Box className={cx(classes.cardHead, classes.cardHeadInWrap)}>
          <Text className={classes.cardHeadTitle}>Section C · Review Questions</Text>
        </Box>
        <Box className={cx(classes.cardBody, classes.cardBodyInWrap)}>
          {draft.questionsC.map((q) => (
            <Box key={q.id} className={classes.qBlock} id={rowAnchorId("C", q.id)}>
              <Text className={classes.qText}>{q.text}</Text>
              <PillToggle
                options={YN_OPTIONS}
                value={q.answer}
                onChange={(v) => patchQuestion(q.id, { answer: v })}
                disabled={locked}
                wide
              />
              {q.answer == null && <RequiresSelection classes={classes} manual={isManual} />}
              <textarea
                className={classes.ta}
                placeholder="Add detail…"
                disabled={locked}
                value={q.note}
                onChange={(e) => patchQuestion(q.id, { note: e.target.value })}
              />
            </Box>
          ))}
        </Box>
      </Box>

      <Box className={classes.cardSegment}>
        <Flex className={cx(classes.cardHead, classes.cardHeadInWrap)}>
          <Text className={classes.cardHeadTitle}>Completion</Text>
          {!locked && (
            <button className={classes.signDocBtn} onClick={onOpenSign}>
              <IconEdit size={15} /> Sign Document
            </button>
          )}
        </Flex>
        <Box className={cx(classes.cardBody, classes.cardBodyInWrap)}>
          <Box>
            <Text className={classes.metaLabel}>ISP revision date(s) during this period</Text>
            {isManual ? (
              <input
                className={classes.inp}
                placeholder="e.g. May 3, 2026"
                value={draft.ispRevisionDates || ""}
                disabled={locked}
                onChange={(e) => patchHeader({ ispRevisionDates: e.target.value })}
              />
            ) : (
              <Text
                className={classes.metaValue}
                color={draft.ispRevisionDates === "None recorded" ? "dimmed" : undefined}
                weight={draft.ispRevisionDates === "None recorded" ? 400 : undefined}
              >
                {draft.ispRevisionDates}
              </Text>
            )}
          </Box>
          <Box className={classes.sigGrid}>
            <Box className={classes.sigCell}>
              <Text className={classes.sigVal}>{draft.signature?.nameLine || ""}</Text>
              <Text className={classes.sigLbl}>Completed by (print)</Text>
            </Box>
            <Box className={classes.sigCell}>
              {isImageSignature(draft.signature?.signature) ? (
                <img src={draft.signature.signature} alt="signature" className={classes.sigImage} />
              ) : (
                <Text className={cx(classes.sigVal, classes.sigScript)}>{draft.signature?.signature || ""}</Text>
              )}
              <Text className={classes.sigLbl}>Signature</Text>
            </Box>
            <Box className={classes.sigCell}>
              <Text className={classes.sigVal}>{draft.signature?.date || ""}</Text>
              <Text className={classes.sigLbl}>Date</Text>
            </Box>
          </Box>
        </Box>
      </Box>
      </Box>
    </Box>
  );
};

export default ReviewDetailView;
